import {IShow, IShowLocalMap} from "@evennt-tech/api-v2-core";
import {
  BankTransferInvoiceProps,
  PDFGenerator,
} from "@evennt-tech/api-v2-pdf-templates";
import {format} from "date-fns";
import {uniqBy} from "lodash";
import {getShowDateFormat} from "constraints/general";
import formatExtraProductName from "tools/formats/formatExtraProductName";
import {CpoConfigById} from "store/custom-product-option/custom-product-option-configs.store";
import {getCpoValue} from "tools/pdf/getCpoValue";
import {getTotalOrderAmount} from "tools/order/getTotalOrderAmount";
import {ShowUtils} from "tools/show";
import {SupplierCompanyUtils} from "tools/supplier-company";
import Fee from "ts/api/fee";
import {ExpandedOrder, Invoice} from "ts/api/order";

const INVOICE_DUE_DAYS = 14;

export type BankTransferInvoicePdfParams = {
  show: IShow;
  order: ExpandedOrder;
  invoice: Invoice;
  localMap: Pick<IShowLocalMap, "stand" | "show">;
  configById?: CpoConfigById;
};

const buildProducts = (
  invoice: Invoice,
  configById?: CpoConfigById
): BankTransferInvoiceProps["products"] =>
  invoice.products.flatMap((product) => {
    const cpoValue = getCpoValue(
      configById,
      product.cpoConfig?.isCustom ? product.cpoConfig : undefined
    );

    const rows: BankTransferInvoiceProps["products"] = [
      {
        name: [product.name, cpoValue].filter(Boolean).join(" "),
        priceWithFees: product.priceWithFees,
        quantity: product.quantity,
      },
    ];

    (product.extras ?? []).forEach((extra) => {
      rows.push({
        name: formatExtraProductName(extra.name),
        priceWithFees: extra.priceWithFees,
        quantity: extra.quantity,
      });
    });

    return rows;
  });

const buildFees = (invoice: Invoice): BankTransferInvoiceProps["fees"] => {
  const fees = uniqBy(
    invoice.products.map((product) => product?.fees).flat(1),
    "id"
  ).filter(Boolean) as Fee[];

  return fees.map((fee) => ({
    name: fee.name,
    amountWithFees: fee.amountWithFees ?? 0,
  }));
};

export const generateBankTransferInvoicePdfBlob = async ({
  show,
  order,
  invoice,
  localMap,
  configById,
}: BankTransferInvoicePdfParams): Promise<Blob | null> => {
  const product = order.products.find(
    (orderProduct) => orderProduct.id === invoice.products[0].id
  );
  if (!product) {
    return null;
  }
  const supplierCompany = order?.supplierCompanies?.find(
    (company) => company.id === product.owner.id
  );
  if (!supplierCompany) {
    return null;
  }

  const dateFormat = getShowDateFormat(show).replace(/\//g, ".");
  const {financialAddresses} = order;

  const props: BankTransferInvoiceProps = {
    invoiceNumber: invoice.invoiceNumber,
    poNumber: invoice.poNumber,
    invoiceDate: format(order.createdAt.toDate(), dateFormat),
    dueDays: INVOICE_DUE_DAYS,
    currency: show.currency,
    salesTax: {
      name: show.salesTaxName,
      percentage: show.salesTaxPercentage,
    },
    supplier: {
      name: supplierCompany.name,
      address: SupplierCompanyUtils.getAddress(supplierCompany, {
        postCode: false,
      }),
      postCode: supplierCompany.postCode,
      VAT: supplierCompany.VAT,
      logo: supplierCompany?.logo?.url,
    },
    billing: {
      name: order.billingDetails.name,
      address: order.billingDetails.address,
      postCode: order.billingDetails.postCode,
      vat: order.billingDetails.vat,
    },
    delivery: {
      standLabel: localMap.stand ?? "",
      standName: order.exhibitorConnection?.stand?.name ?? "",
      showName: show.name,
      showAddress: ShowUtils.getAddress(show),
      showPostcode: show.postcode,
    },
    showLabel: localMap.show ?? "",
    products: buildProducts(invoice, configById),
    fees: buildFees(invoice),
    discountAmount: invoice.discountAmount,
    paymentFeeAmount: invoice.paymentFeeAmount,
    subtotalAmount: invoice.subtotalAmount,
    taxAmount: invoice.taxAmount,
    totalAmount: getTotalOrderAmount(invoice.totalAmount, show.currency),
    financialAddresses: (financialAddresses as unknown) as BankTransferInvoiceProps["financialAddresses"],
  };

  return PDFGenerator.bankTransferInvoicePdfBlob(props);
};
